import styles from "./SyncMemberDataModal.module.scss";
import Modal from "../../Common/Modal/Modal";
import Accordion from "../../Common/Accordion/Accordion";
import React from "react";

interface SyncItem {
  tenantId?: number | null;
  email?: string | null;
  action?: string | null;
  message?: string | null;
}

interface SyncResultData {
  success?: boolean | null;
  message?: string | null;
  totalFetched?: number | null;
  skippedNotCurrent?: number | null;
  skippedNoAddress?: number | null;
  skippedNoEmail?: number | null;
  skippedNoBuilding?: number | null;
  updated?: number | null;
  added?: number | null;
  failed?: number | null;
  items?: SyncItem[] | null;
}

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  data?: SyncResultData | null;
}

const ACTION_ORDER = ["added", "updated", "skipped", "failed"] as const;

const ACTION_COLORS: Record<string, string> = {
  added: "#2d8a1f",
  updated: "#1a6bb5",
  skipped: "#b8860b",
  failed: "#d64545",
};

const SUMMARY_STATS: {
  key: keyof SyncResultData;
  label: string;
}[] = [
  { key: "totalFetched", label: "Total Fetched" },
  { key: "added", label: "Added" },
  { key: "updated", label: "Updated" },
  { key: "failed", label: "Failed" },
  { key: "skippedNotCurrent", label: "Skipped (Not Current)" },
  { key: "skippedNoAddress", label: "Skipped (No Address)" },
  { key: "skippedNoEmail", label: "Skipped (No Email)" },
  { key: "skippedNoBuilding", label: "Skipped (No Building)" },
];

function SyncMemberDataModal(props: Props) {
  const { isOpen, setIsOpen, data } = props ?? {};

  if (!isOpen) return null;

  const groupedItems: Record<string, SyncItem[]> = {};
  const rawItems = Array.isArray(data?.items) ? data?.items : [];

  rawItems?.forEach((item) => {
    if (!item || typeof item !== "object") return;

    const action =
      item?.action?.toString?.()?.trim?.()?.toLowerCase?.() || "other";
    if (!groupedItems[action]) groupedItems[action] = [];
    groupedItems[action]?.push(item);
  });

  const knownActions = ACTION_ORDER.filter(
    (action) => (groupedItems?.[action]?.length ?? 0) > 0,
  );
  const extraActions = Object.keys(groupedItems ?? {}).filter(
    (action) =>
      !ACTION_ORDER.includes(action as (typeof ACTION_ORDER)[number]) &&
      (groupedItems?.[action]?.length ?? 0) > 0,
  );
  const accordionActions = [...knownActions, ...extraActions];

  const formatActionTitle = (action?: string | null, count?: number | null) => {
    const safeAction = action?.toString?.()?.trim?.() || "other";
    const label = `${safeAction.charAt(0).toUpperCase()}${safeAction.slice(1)}`;
    return `${label} (${count ?? 0})`;
  };

  const hasAnyStats = SUMMARY_STATS.some(
    ({ key }) => typeof data?.[key] === "number",
  );

  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen} showCloseIcon>
      <div className={styles.mainContainer}>
        <div className={styles.modalHeader}>
          <p className={styles.modalTitle}>Sync Result</p>
          {data?.message ? (
            <p
              className={`${styles.message} ${
                data?.success ? styles.messageSuccess : styles.messageError
              }`}
            >
              {data?.message}
            </p>
          ) : null}
          {hasAnyStats ? (
            <div className={styles.statsRow}>
              {SUMMARY_STATS.map(({ key, label }) => {
                const value = data?.[key];
                if (typeof value !== "number") return null;

                const isFailed = key === "failed" && value > 0;
                const isSuccess =
                  (key === "added" || key === "updated") && value > 0;

                return (
                  <span
                    key={key}
                    className={`${styles.stat} ${
                      isFailed
                        ? styles.statError
                        : isSuccess
                          ? styles.statSuccess
                          : ""
                    }`}
                  >
                    {label}: <strong>{value}</strong>
                  </span>
                );
              })}
            </div>
          ) : null}
        </div>

        <div className={styles.modalBody}>
          {!data ? (
            <p className={styles.emptyText}>No sync data available</p>
          ) : accordionActions?.length === 0 ? (
            <p className={styles.emptyText}>No item details available</p>
          ) : (
            accordionActions.map((action) => {
              const items = groupedItems?.[action] ?? [];
              return (
                <div key={action} className={styles.accordionContainer}>
                  <Accordion
                    title={formatActionTitle(action, items?.length)}
                    color={ACTION_COLORS?.[action] || "#000000"}
                    isEmpty={(items?.length ?? 0) === 0}
                  >
                    <div className={styles.itemsList}>
                      {items?.map?.((item, index) => {
                        if (!item) return null;

                        const tenantId = item?.tenantId ?? "N/A";
                        const email = item?.email?.trim?.() || "No email";
                        const message = item?.message?.trim?.() || "";
                        const itemAction = item?.action ?? action ?? "other";

                        return (
                          <div
                            key={`${tenantId}-${itemAction}-${index}`}
                            className={styles.itemRow}
                          >
                            <div className={styles.itemMain}>
                              <span className={styles.tenantId}>
                                #{tenantId}
                              </span>
                              <span className={styles.email}>{email}</span>
                            </div>
                            {message ? (
                              <p className={styles.itemMessage}>{message}</p>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </Accordion>
                </div>
              );
            })
          )}
        </div>

        <div className={styles.modalFooter}>
          <button
            className={styles.closeButton}
            onClick={() => setIsOpen?.(false)}
            type="button"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default SyncMemberDataModal;
